import { notifications } from '@mantine/notifications'
import * as Sentry from '@sentry/react'

interface ApiErrorResponse {
  message?: string
  error?: string
  statusCode?: number
}

interface ErrorHandlerOptions {
  showNotification?: boolean
  logToSentry?: boolean
  customMessage?: string
}

const defaultOptions: ErrorHandlerOptions = {
  showNotification: true,
  logToSentry: true,
}

const isHttpError = (error: unknown): error is { response: Response; name: string; data?: unknown } => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    'name' in error &&
    (error as { name: string }).name === 'HTTPError'
  )
}

export const getErrorMessage = async (error: unknown): Promise<string> => {
  if (isHttpError(error)) {
    try {
      const body = error.data as ApiErrorResponse | undefined
      return body?.message || body?.error || `Request failed with status ${error.response.status}`
    } catch {
      return `Request failed with status ${error.response.status}`
    }
  }

  if (error instanceof Error) {
    return error.message
  }

  return 'An unexpected error occurred'
}

export const handleApiError = async (error: unknown, options: ErrorHandlerOptions = {}): Promise<string> => {
  const opts = { ...defaultOptions, ...options }
  const message = opts.customMessage || (await getErrorMessage(error))

  if (opts.logToSentry && error instanceof Error) {
    Sentry.captureException(error)
  }

  if (opts.showNotification) {
    notifications.show({
      title: 'Error',
      message,
      color: 'red',
      autoClose: 5000,
    })
  }

  return message
}

export const handleMutationError = async (error: unknown, action: string): Promise<void> => {
  const message = await getErrorMessage(error)

  notifications.show({
    title: `Failed to ${action}`,
    message,
    color: 'red',
    autoClose: 5000,
  })

  if (error instanceof Error) {
    Sentry.captureException(error, { extra: { action } })
  }
}

export const createQueryErrorHandler = (context: string) => {
  return async (error: unknown) => {
    console.error(`[${context}] Query error:`, error)

    if (error instanceof Error) {
      Sentry.captureException(error, { extra: { context } })
    }
  }
}
